import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import {
  getAvailabilitySettingListingApi,
  getClinicListApi,
} from "@/utils/apis/doctor/availabilitySetting";

import {
  IGetAvailabilitySettingResponse,
  IAvailabilitySettingResult,
  IAvailabilitySetting,
  IClinicListResponse,
} from "@/models/ApiRequestResponse/doctor";
import { GBSCError } from "@/models/GBSCError";

import { RootState } from "@/store/store";

import { ILabelValue } from "@/models";
import { IWeekDay } from "@/models/doctor";

interface IDayDateFilter {
  day: IWeekDay;
  date: {
    start: string;
    end: string;
  };
}

export const fetchAvailabilitySettingsListing = createAsyncThunk<
  IGetAvailabilitySettingResponse | GBSCError,
  { day?: string; start?: string; end?: string }
>("doctor/availabilitySettingsListing", async ({ day, start, end }) => {
  const response = await getAvailabilitySettingListingApi({
    day,
    start,
    end,
  });
  return response;
});

export const fetchClinicList = createAsyncThunk<
  IClinicListResponse | GBSCError,
  void
>("doctor/clinicList", async () => {
  const response = await getClinicListApi();
  return response;
});

const availabilitySettingSlice = createSlice({
  name: "availabilitySetting",
  initialState: {
    availabilitySettingsListing: [] as IAvailabilitySettingResult[],
    selectedAvailabilitySetting: null as IAvailabilitySettingResult | null,
    selectedAvailabilitySettingDetail: null as IAvailabilitySetting | null,
    isDeleteAvailability: false as boolean,
    isLoading: true as boolean,
    isBlockedBookingShown: false as boolean,
    clinicList: [] as ILabelValue[],
    isEditAvailability: false as boolean,
    dayDateFilter: {} as IDayDateFilter,
  },
  reducers: {
    setIsDeleteAvailability: (state, action) => {
      state.isDeleteAvailability = action.payload;
    },
    setIsBlockedBookingShown: (state, action) => {
      state.isBlockedBookingShown = action.payload;
    },
    setSelectedAvailabilitySetting: (state, action) => {
      state.selectedAvailabilitySetting = action.payload;
    },
    setSelectedAvailabilitySettingDetail: (state, action) => {
      state.selectedAvailabilitySettingDetail = action.payload;
    },
    setBlockedBookingAvailabilitySetting: (state, action) => {
      const { blockedRoles, availabilitySettingId } = action.payload;
      // Update the selectedAvailabilitySettingDetail as well
      state.selectedAvailabilitySettingDetail = {
        ...state.selectedAvailabilitySettingDetail,
        blocked_roles: blockedRoles,
      };

      // Update the selectedAvailabilitySetting schedules as well
      let selectedAvailabilitySetting =
        state.selectedAvailabilitySetting.schedules.find(
          (schedule) => schedule.id === availabilitySettingId
        );
      selectedAvailabilitySetting = {
        ...selectedAvailabilitySetting,
        blocked_roles: blockedRoles,
      };

      state.selectedAvailabilitySetting = {
        ...state.selectedAvailabilitySetting,
        schedules: state.selectedAvailabilitySetting.schedules.map((schedule) =>
          schedule.id === availabilitySettingId
            ? selectedAvailabilitySetting
            : schedule
        ),
      };

      // Update the availabilitySettingsListing as well
      state.availabilitySettingsListing = state.availabilitySettingsListing.map(
        (item) =>
          item.clinic_id === state.selectedAvailabilitySetting.clinic_id
            ? {
                ...item,
                schedules: item.schedules.map((schedule) =>
                  schedule.id === availabilitySettingId
                    ? selectedAvailabilitySetting
                    : schedule
                ),
              }
            : item
      );
    },
    setIsEditAvailability: (state, action) => {
      state.isEditAvailability = action.payload;
    },
    setDayDateFilter: (state, action) => {
      state.dayDateFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailabilitySettingsListing.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAvailabilitySettingsListing.fulfilled, (state, action) => {
        state.isLoading = false;
        if ("result" in action.payload) {
          state.availabilitySettingsListing = action.payload.result;
        }
      })
      .addCase(fetchAvailabilitySettingsListing.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchClinicList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchClinicList.fulfilled, (state, action) => {
        state.isLoading = false;
        if ("result" in action.payload) {
          const result = action.payload.result.map((item, index) => ({
            label: item.name,
            value: `${item.id}_${item.consultationFee}`,
          }));
          state.clinicList = result;
        }
      })
      .addCase(fetchClinicList.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

const selectAvailabilitySettings = (state: RootState) => state.availability;

export const availabilitySettings = createSelector(
  [selectAvailabilitySettings],
  (selectAvailabilitySettings) => {
    return {
      isDeleteAvailability: selectAvailabilitySettings.isDeleteAvailability,
      isLoading: selectAvailabilitySettings.isLoading,
      isBlockedBookingShown: selectAvailabilitySettings.isBlockedBookingShown,
      availabilitySettingsListing:
        selectAvailabilitySettings.availabilitySettingsListing,
      selectedAvailabilitySetting:
        selectAvailabilitySettings.selectedAvailabilitySetting,
      selectedAvailabilitySettingDetail:
        selectAvailabilitySettings.selectedAvailabilitySettingDetail,
      clinicList: selectAvailabilitySettings.clinicList,
      isEditAvailability: selectAvailabilitySettings.isEditAvailability,
      dayDateFilter: selectAvailabilitySettings.dayDateFilter,
    };
  }
);

export const {
  setIsDeleteAvailability,
  setIsBlockedBookingShown,
  setSelectedAvailabilitySetting,
  setSelectedAvailabilitySettingDetail,
  setBlockedBookingAvailabilitySetting,
  setIsEditAvailability,
  setDayDateFilter,
} = availabilitySettingSlice.actions;
export default availabilitySettingSlice.reducer;
